import React, { useState } from 'react';
import {
    Card, Table, Tag, Typography, Button, Space, DatePicker, Drawer, Descriptions, message
} from 'antd';
import { FileTextOutlined, DownloadOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// TODO: 联调时替换为接口数据
// 云主机实例清单（复用腾讯云 CVM 管理维度：机型/规格/数量）
const TIER_2 = [
    { label: '应用服务器', instanceType: '标准型S5', spec: '4C8G', disk: 500, count: 2 },
    { label: '数据库服务器', instanceType: '标准型S5', spec: '4C8G', disk: 500, count: 1 },
];
const TIER_3 = [
    { label: '应用服务器', instanceType: '标准型S5', spec: '8C16G', disk: 1000, count: 4 },
    { label: '数据库服务器', instanceType: '标准型S5', spec: '8C16G', disk: 1000, count: 2 },
];
const TIER_4 = [
    { label: '应用服务器', instanceType: '标准型S5', spec: '16C32G', disk: 2000, count: 8 },
    { label: '数据库服务器', instanceType: '标准型S5', spec: '16C32G', disk: 2000, count: 3 },
];
const renderInstanceSummary = (config) => {
    if (!config?.instances?.length) return '--';
    return config.instances.map(i => `${i.count}×${i.label} ${i.spec}/${i.disk}GB`).join(' + ');
};
// 企业月租需付 = 平台成本 × 月租费成本比；未配商务返回 0
const calcEnterpriseMonthlyCost = (record) => {
    const ratio = record?.bizConfig?.monthlyRentRatio;
    if (ratio == null || ratio <= 0) return 0;
    return Number((record.platformCost * ratio).toFixed(2));
};
const formatDeviceCount = (n) => {
    if (!n && n !== 0) return '--';
    if (n >= 10000 && n % 10000 === 0) return `${n / 10000}w`;
    if (n >= 10000) return `${(n / 10000).toFixed(1)}w`;
    return n?.toLocaleString();
};

// 需付企业列表（已部署产生账单的企业）
const MOCK_BILL_LIST = [
    { key: '1', deployNo: 'DEP-test-A04', companyName: 'test-杭州数智网络', companyId: 'test-HZ999',
        region: '南京', deviceCount: 3000, config: { instances: TIER_2 }, platformCost: 3200.00,
        bizConfig: { monthlyRentRatio: 1.4, elasticUsageRatio: 1.2 }, deployTime: '2026-04-18 12:00:00' },
    { key: '2', deployNo: 'DEP-test-A08', companyName: 'test-深圳云端科技', companyId: 'test-SZ666',
        region: '广州', deviceCount: 5000, config: { instances: TIER_4 }, platformCost: 15000.00,
        bizConfig: { monthlyRentRatio: 1.6, elasticUsageRatio: 1.3 }, deployTime: '2026-03-16 09:00:00' },
    { key: '3', deployNo: 'DEP-test-A03', companyName: 'test-北京云联科技', companyId: 'test-BJ666',
        region: '北京', deviceCount: 80000, config: { instances: TIER_4 }, platformCost: 15000.00,
        bizConfig: { monthlyRentRatio: 1.6, elasticUsageRatio: 1.3 }, deployTime: '2026-06-19 14:00:00' },
    { key: '4', deployNo: 'DEP-test-A07', companyName: 'test-武汉光谷科技', companyId: 'test-WH222',
        region: '北京', deviceCount: 35000, config: { instances: TIER_3 }, platformCost: 6500.00,
        bizConfig: { monthlyRentRatio: null, elasticUsageRatio: null }, deployTime: '2026-06-23 09:00:00' },
    { key: '5', deployNo: 'DEP-test-A06', companyName: 'test-上海智科信息', companyId: 'test-SH777',
        region: '上海', deviceCount: 15000, config: { instances: TIER_3 }, platformCost: 6800.00,
        bizConfig: { monthlyRentRatio: 1.5, elasticUsageRatio: 1.2 }, deployTime: '2026-06-20 15:00:00' },
];

// 弹性账单 mock（运维推送 → 直接展示；管理端可见成本单价与平台成本）
const MOCK_ELASTIC_BILLS = [
    { key: 'eb-01', billNo: 'CLOUD20260715001', deployKey: '1', billingDate: '2026-07-15', itemName: 'COS存储', costUnitPrice: 0.60, usage: 123.4567 },
    { key: 'eb-02', billNo: 'CLOUD20260716001', deployKey: '1', billingDate: '2026-07-16', itemName: 'COS存储', costUnitPrice: 0.60, usage: 98.2341 },
    { key: 'eb-03', billNo: 'CLOUD20260717001', deployKey: '1', billingDate: '2026-07-17', itemName: 'CDN流量', costUnitPrice: 0.40, usage: 210.7890 },
    { key: 'eb-04', billNo: 'CLOUD20260718001', deployKey: '1', billingDate: '2026-07-18', itemName: 'COS存储', costUnitPrice: 0.60, usage: 156.7890 },
    { key: 'eb-05', billNo: 'CLOUD20260719001', deployKey: '1', billingDate: '2026-07-19', itemName: '动态带宽', costUnitPrice: 1.00, usage: 45.1234 },
    { key: 'eb-06', billNo: 'CLOUD20260720001', deployKey: '1', billingDate: '2026-07-20', itemName: 'COS存储', costUnitPrice: 0.60, usage: 88.6600 },
    { key: 'eb-07', billNo: 'CLOUD20260801001', deployKey: '1', billingDate: '2026-08-01', itemName: 'CDN流量', costUnitPrice: 0.40, usage: 302.1234 },
    { key: 'eb-08', billNo: 'CLOUD20260802001', deployKey: '1', billingDate: '2026-08-02', itemName: 'COS存储', costUnitPrice: 0.60, usage: 140.5000 },
];
const getElasticBills = (deployKey) => MOCK_ELASTIC_BILLS.filter(c => c.deployKey === deployKey);

// 基础配置账单（首月非整月 → 升配拆分 → 按实际天数计费）
const generateBills = (record) => {
    if (!record?.deployTime || !record?.platformCost) return [];
    const bills = [
        { key: 'bill-01', billNo: 'MON202603', month: '2026-03', billingDate: '2026-04-01', deviceCount: 5000,
            configLabel: '4C8G / 500G / RDS:4C8G500G×2', serviceDuration: '16/31 天',
            enterpriseUnit: 3500.00, enterprisePayable: 1806.45, platformUnit: 3200.00, platformPayable: 1651.61 },
        { key: 'bill-02', billNo: 'MON202604', month: '2026-04', billingDate: '2026-05-01', deviceCount: 5000,
            configLabel: '4C8G / 500G / RDS:4C8G500G×2', serviceDuration: '30/30 天',
            enterpriseUnit: 3500.00, enterprisePayable: 3500.00, platformUnit: 3200.00, platformPayable: 3200.00 },
        { key: 'bill-03', billNo: 'MON202605A', month: '2026-05(上)', billingDate: '2026-06-01', deviceCount: 5000,
            configLabel: '4C8G / 500G / RDS:4C8G500G×2', serviceDuration: '10/31 天',
            enterpriseUnit: 3500.00, enterprisePayable: 1129.03, platformUnit: 3200.00, platformPayable: 1032.26 },
        { key: 'bill-04', billNo: 'MON202605B', month: '2026-05(下)', billingDate: '2026-06-01', deviceCount: 20000,
            configLabel: '8C16G / 1T / RDS:8C16G1T×2', serviceDuration: '21/31 天',
            enterpriseUnit: 7200.00, enterprisePayable: 4877.42, platformUnit: 6800.00, platformPayable: 4606.45 },
        { key: 'bill-05', billNo: 'MON202606A', month: '2026-06(上)', billingDate: '2026-07-01', deviceCount: 20000,
            configLabel: '8C16G / 1T / RDS:8C16G1T×2', serviceDuration: '20/30 天',
            enterpriseUnit: 7200.00, enterprisePayable: 4800.00, platformUnit: 6800.00, platformPayable: 4533.33 },
        { key: 'bill-06', billNo: 'MON202606B', month: '2026-06(下)', billingDate: '2026-07-01', deviceCount: 80000,
            configLabel: '16C32G / 2T / RDS:16C32G2T×3', serviceDuration: '10/30 天',
            enterpriseUnit: 16000.00, enterprisePayable: 5333.33, platformUnit: 15000.00, platformPayable: 5000.00 },
        { key: 'bill-07', billNo: 'MON202607', month: '2026-07', billingDate: '2026-08-01', deviceCount: 80000,
            configLabel: '16C32G / 2T / RDS:16C32G2T×3', serviceDuration: '20/31 天',
            enterpriseUnit: 16000.00, enterprisePayable: 10322.58, platformUnit: 15000.00, platformPayable: 9677.42 },
    ];
    return bills.reverse();
};

const PrivateDeployBill = () => {
    const [billDrawer, setBillDrawer] = useState({ open: false, record: null });
    const [elasticRange, setElasticRange] = useState([]);

    // Mock 导出：联调时替换为接口导出
    const handleExport = () => {
        message.loading('正在导出数据...');
        setTimeout(() => message.success('导出成功'), 800);
    };

    const columns = [
        { title: '需付企业', key: 'company', width: 200,
            render: (_, r) => <div><Text strong style={{ fontSize: 13 }}>{r.companyName}</Text><br /><Text type="secondary" style={{ fontSize: 11 }}>{r.companyId}</Text></div> },
        { title: '服务区域', dataIndex: 'region', width: 100 },
        { title: '最大设备数', dataIndex: 'deviceCount', width: 110, render: (v) => formatDeviceCount(v) },
        { title: '服务器配置', key: 'config', width: 220, render: (_, r) => <Text style={{ fontSize: 12 }}>{renderInstanceSummary(r.config)}</Text> },
        { title: '平台月租成本', key: 'platformCost', width: 130,
            render: (_, r) => <Text>¥ {r.platformCost?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Text> },
        { title: '企业月租需付', key: 'enterpriseCost', width: 150,
            render: (_, r) => {
                if (r.bizConfig?.monthlyRentRatio == null || r.bizConfig?.monthlyRentRatio <= 0) {
                    return <span><Text type="danger">¥ 0.00</Text> <Tag color="red" style={{ border: 'none', fontSize: 11 }}>未配商务</Tag></span>;
                }
                return <Text>¥ {calcEnterpriseMonthlyCost(r).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Text>;
            } },
        { title: '操作', key: 'action', width: 110, fixed: 'right',
            render: (_, r) => <Button type="link" size="small" icon={<FileTextOutlined />} onClick={() => setBillDrawer({ open: true, record: r })}>查看账单</Button> },
    ];

    return (
        <div style={{ padding: 24 }}>
            <div style={{ marginBottom: 16 }}><Title level={4} style={{ margin: 0 }}>私有部署账单</Title></div>
            <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                <Table columns={columns} dataSource={MOCK_BILL_LIST} rowKey="key"
                    pagination={{ showSizeChanger: true, showTotal: (t) => `共 ${t} 条记录` }}
                    scroll={{ x: 1120 }} />
            </Card>

            {/* ── 账单 Drawer：基础配置账单 + 弹性账单 ── */}
            <Drawer title="费用账单" open={billDrawer.open} onClose={() => setBillDrawer({ open: false, record: null })} width={960}>
                <Descriptions column={2} size="small" bordered style={{ marginBottom: 12 }}>
                    <Descriptions.Item label="需付企业">{billDrawer.record?.companyName}</Descriptions.Item>
                    <Descriptions.Item label="当前设备数">{billDrawer.record?.deviceCount != null ? formatDeviceCount(billDrawer.record.deviceCount) : '--'}</Descriptions.Item>
                    <Descriptions.Item label="当前平台月租成本">¥ {billDrawer.record?.platformCost?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Descriptions.Item>
                    <Descriptions.Item label="当前企业月租需付">
                        {billDrawer.record?.bizConfig?.monthlyRentRatio != null && billDrawer.record?.bizConfig?.monthlyRentRatio > 0
                            ? `¥ ${calcEnterpriseMonthlyCost(billDrawer.record).toLocaleString(undefined, { minimumFractionDigits: 2 })}`
                            : <Text type="danger">未配置商务</Text>}
                    </Descriptions.Item>
                </Descriptions>

                <Descriptions size="small" bordered style={{ marginBottom: 12 }} column={1}>
                    <Descriptions.Item label="服务器配置">{renderInstanceSummary(billDrawer.record?.config)}</Descriptions.Item>
                </Descriptions>

                <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text strong style={{ fontSize: 14 }}>基础配置账单</Text>
                    <Button icon={<DownloadOutlined />} onClick={handleExport}>导出</Button>
                </div>
                <Table dataSource={generateBills(billDrawer.record)} size="small"
                    scroll={{ x: 'max-content' }}
                    pagination={{ pageSize: 12, showTotal: (t) => `共 ${t} 条账单` }}
                    columns={[
                        { title: '账单编号', dataIndex: 'billNo', width: 140, render: (v) => <Text style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</Text> },
                        { title: '结算月份', dataIndex: 'month', width: 100 },
                        { title: '出账日', dataIndex: 'billingDate', width: 90 },
                        { title: '设备数', dataIndex: 'deviceCount', width: 80, render: (v) => formatDeviceCount(v) },
                        { title: '服务器配置', dataIndex: 'configLabel', width: 180, render: (v) => <Text style={{ fontSize: 11 }}>{v}</Text> },
                        { title: '服务时长', dataIndex: 'serviceDuration', width: 85 },
                        { title: '平台成本/月', dataIndex: 'platformUnit', width: 110, render: (v) => <Text>¥ {v?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Text> },
                        { title: '企业需付/月', dataIndex: 'enterpriseUnit', width: 110, render: (v) => <Text>¥ {v?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Text> },
                        { title: '应付金额', dataIndex: 'enterprisePayable', width: 110, render: (v) => <Text strong>¥ {v?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Text> },
                    ]}
                    summary={() => {
                        const bills = generateBills(billDrawer.record);
                        const totalEnterprise = bills.reduce((s, b) => s + (b.enterprisePayable || 0), 0);
                        return (
                            <Table.Summary.Row>
                                <Table.Summary.Cell colSpan={8}><Text strong>基础配置累计应付</Text></Table.Summary.Cell>
                                <Table.Summary.Cell>
                                    <Text strong style={{ color: '#1890ff' }}>¥ {totalEnterprise.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Text>
                                </Table.Summary.Cell>
                            </Table.Summary.Row>
                        );
                    }} />

                <div style={{ margin: '16px 0 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text strong style={{ fontSize: 14 }}>弹性账单</Text>
                    <Button icon={<DownloadOutlined />} onClick={handleExport}>导出</Button>
                </div>
                {(() => {
                    const bills = [...getElasticBills(billDrawer.record?.key)].sort((a, b) => (a.billingDate < b.billingDate ? 1 : -1));
                    if (bills.length === 0) return (
                        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}><Text type="secondary">暂无弹性账单记录</Text></div>
                    );
                    const ratio = billDrawer.record?.bizConfig?.elasticUsageRatio;
                    const hasRatio = ratio != null && ratio > 0;
                    const filtered = elasticRange.length === 2
                        ? bills.filter(b => b.billingDate >= elasticRange[0].format('YYYY-MM-DD') && b.billingDate <= elasticRange[1].format('YYYY-MM-DD'))
                        : bills;
                    const totalPlatform = filtered.reduce((s, b) => s + (b.costUnitPrice * b.usage || 0), 0);
                    const totalEnterprise = filtered.reduce((s, b) => s + (hasRatio ? b.costUnitPrice * ratio * b.usage : 0), 0);
                    return (
                        <>
                            <Space style={{ marginBottom: 12 }}>
                                <Text type="secondary">出账日区间：</Text>
                                <RangePicker onChange={(dates) => setElasticRange(dates || [])} />
                            </Space>
                            <Table dataSource={filtered} size="small"
                                pagination={{ pageSize: 12, showTotal: (t) => `共 ${t} 条` }}
                                columns={[
                                    { title: '账单编号', dataIndex: 'billNo', width: 140, render: (v) => <Text style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</Text> },
                                    { title: '出账日', dataIndex: 'billingDate', width: 110 },
                                    { title: '费用项', dataIndex: 'itemName', width: 120, render: (v) => <Tag color="purple" style={{ border: 'none' }}>{v}</Tag> },
                                    { title: '成本单价（元/G）', dataIndex: 'costUnitPrice', width: 130, render: (v) => <Text>¥ {v?.toFixed(2)}</Text> },
                                    { title: '用量（G）', dataIndex: 'usage', width: 130, render: (v) => <Text>{v?.toFixed(4)}</Text> },
                                    { title: '平台成本', key: 'platformCost', width: 110, render: (_, b) => <Text>¥ {(b.costUnitPrice * b.usage).toFixed(2)}</Text> },
                                    { title: '企业需付', key: 'enterpriseCost', width: 110, render: (_, b) => hasRatio ? <Text strong>¥ {(b.costUnitPrice * ratio * b.usage).toFixed(2)}</Text> : <Text type="danger">¥ 0.00</Text> },
                                ]}
                                summary={() => (
                                    <Table.Summary.Row>
                                        <Table.Summary.Cell colSpan={5}><Text strong>汇总金额（按查询条件）</Text></Table.Summary.Cell>
                                        <Table.Summary.Cell><Text strong>¥ {totalPlatform.toFixed(2)}</Text></Table.Summary.Cell>
                                        <Table.Summary.Cell><Text strong style={{ color: '#fa541c' }}>¥ {totalEnterprise.toFixed(2)}</Text></Table.Summary.Cell>
                                    </Table.Summary.Row>
                                )} />
                        </>
                    );
                })()}
            </Drawer>
        </div>
    );
};

export default PrivateDeployBill;
